import { useEffect, useState } from 'react';
import clsx from 'clsx';

import ColorSwatchGrid from '@atom/components/common/colorSwatchGrid/ColorSwatchGrid';
import { Button, Modal, Progress, Snackbar, TextField } from '@atom/mui';
import styleColors from '@atom/styles/colors';
import type { ArcgisIconType, ArcgisLayer } from '@atom/types/arcgis';
import { getGeospatialColorSwatchList } from '@atom/utilities/colorUtilities';

import ArcgisLayerIcon from '../arcgis/ArcgisLayerIcon';
import { useArcgisLayers } from '../arcgis/ArcgisLayersProvider';
import { detectService } from '../arcgis/arcgisService';

import classes from './filterPanel.module.css';

const ARCGIS_ICON_TYPES: ArcgisIconType[] = ['SHAPE', 'LINE', 'POINT'];

const styles = {
  content: { padding: '0' },
  spinner: { marginRight: '0.5rem' },
};

type GeospatialLayerModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: (layer: ArcgisLayer) => void;
  layer?: ArcgisLayer;
  readOnly?: boolean;
};

const GeospatialLayerModal = ({
  open,
  onClose,
  onSaved,
  layer,
  readOnly = false,
}: GeospatialLayerModalProps) => {
  const { add, update } = useArcgisLayers();

  const isEdit = Boolean(layer);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [iconType, setIconType] = useState<ArcgisIconType>('SHAPE');
  const [colorId, setColorId] = useState(0);
  const [saving, setSaving] = useState(false);

  const colors = getGeospatialColorSwatchList();

  const failureMessage = `Unable to ${
    isEdit ? 'update' : 'add'
  } the geospatial layer. Please provide a valid ArcGIS service URL.`;

  const title = readOnly
    ? 'Geospatial Layer'
    : isEdit
      ? 'Edit Geospatial Layer'
      : 'Add Geospatial Layer';

  const dataCyPrefix = readOnly
    ? 'viewGeospatialLayerModal'
    : isEdit
      ? 'editGeospatialLayerModal'
      : 'addGeospatialLayerModal';

  useEffect(() => {
    if (open) {
      setName(layer?.name ?? '');
      setUrl(layer?.link ?? '');
      setIconType(layer?.iconType ?? 'SHAPE');
      setColorId(layer?.colorId ?? 0);
      setSaving(false);
    }
  }, [open]);

  const handleCancel = () => {
    onClose();
  };

  const handleConfirm = async () => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl || !name) {
      return;
    }

    setSaving(true);

    try {
      const linkChanged = !layer || trimmedUrl !== layer.link;

      // Only validate the URL when it actually changed
      if (linkChanged) {
        const serviceType = await detectService(trimmedUrl);

        if (serviceType === 'unsupported') {
          Snackbar.error({ message: failureMessage });
          setSaving(false);
          return;
        }
      }

      const saved = layer
        ? await update({
            id: layer.id,
            name,
            link: trimmedUrl,
            colorId,
            iconType,
          })
        : await add({ name, link: trimmedUrl, colorId, iconType });

      if (!saved) {
        Snackbar.error({ message: failureMessage });
        setSaving(false);
        return;
      }

      onSaved?.(saved);
      Snackbar.info({
        message: `"${saved.name}" ${isEdit ? 'updated' : 'added'}`,
      });
      onClose();
    } catch {
      Snackbar.error({ message: failureMessage });
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      contentStyle={styles.content}
      onCancel={handleCancel}
      data-cy={dataCyPrefix}
      footer={
        <>
          {saving && (
            <Progress
              size={20}
              thickness={4}
              style={styles.spinner}
              data-cy={`${dataCyPrefix}Spinner`}
            />
          )}
          <Button
            onClick={handleCancel}
            data-cy={`${dataCyPrefix}CancelButton`}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={!url.trim() || !name || saving}
              data-cy={`${dataCyPrefix}SaveButton`}
            >
              Save
            </Button>
          )}
        </>
      }
    >
      <div className={classes.modalFields}>
        <div>
          <div className={classes.modalFieldLabel}>Icon</div>
          <div
            className={clsx(
              classes.iconPicker,
              readOnly && classes.pickerDisabled,
            )}
          >
            {ARCGIS_ICON_TYPES.map(type => (
              <div
                key={type}
                className={clsx(
                  classes.iconOption,
                  type === iconType && classes.iconOptionSelected,
                )}
                onClick={() => setIconType(type)}
                data-cy={`geospatialIconOption-${type}`}
              >
                <ArcgisLayerIcon iconType={type} colorId={colorId} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className={classes.modalFieldLabel}>Color</div>
          <div className={clsx(readOnly && classes.pickerDisabled)}>
            <ColorSwatchGrid
              colorId={colorId}
              colors={colors}
              onChange={setColorId}
            />
          </div>
        </div>
        <TextField
          label="Name"
          inputLabelColor={styleColors.neutral.dim}
          value={name}
          onChange={event => setName(event.target.value)}
          placeholder="Layer name"
          slotProps={{ input: { readOnly } }}
        />
        <TextField
          label="ArcGIS service URL"
          inputLabelColor={styleColors.neutral.dim}
          value={url}
          onChange={event => setUrl(event.target.value)}
          placeholder="https://…/FeatureServer"
          slotProps={{ input: { readOnly } }}
        />
      </div>
    </Modal>
  );
};

export default GeospatialLayerModal;
